import { MenuRecipe } from '../queries/common/types'
import type { MenuRecipeTileResponse } from '../queries/menuApiRecipeTile/types'
import { Recipe, RecipeUUID } from './types'

type APINutritionalInformation = {
  per_portion: {
    energy_kcal: number
    carbs_mg: number
    fat_mg: number
    fibre_mg?: number | null
    protein_mg: number
    salt_mg: number
  }
}

type APIPortions =
  | {
      num_portions: number
      is_available: boolean
    }[]
  | undefined

const mapMerchandisingTags = (merchandisingTags: MenuRecipe['merchandising_tags']) => ({
  availability: merchandisingTags.availability
    ? {
        name: merchandisingTags.availability.name,
        color: merchandisingTags.availability.color,
        backgroundColor: merchandisingTags.availability.background_color,
      }
    : null,
  tagline: merchandisingTags.tagline
    ? {
        name: merchandisingTags.tagline.name,
        color: merchandisingTags.tagline.color,
        backgroundColor: merchandisingTags.tagline.background_color,
      }
    : null,
})

const mapNutritionalInformation = (
  nutritionalInformation: APINutritionalInformation,
): Recipe['nutritionalInformation'] => ({
  perPortion: {
    energyKcal: nutritionalInformation.per_portion.energy_kcal,
    carbsMg: nutritionalInformation.per_portion.carbs_mg,
    fatMg: nutritionalInformation.per_portion.fat_mg,
    fibreMg: nutritionalInformation.per_portion.fibre_mg ?? null,
    proteinMg: nutritionalInformation.per_portion.protein_mg,
    saltMg: nutritionalInformation.per_portion.salt_mg,
  },
})

const mapPortions = (portions: APIPortions): Recipe['portions'] =>
  portions?.map((portion) => ({
    numPortions: portion.num_portions,
    isAvailable: portion.is_available,
  }))

const mapShelfLife = (shelfLife: MenuRecipe['shelf_life']): Recipe['shelfLife'] =>
  shelfLife
    ? {
        maxDays: shelfLife.max_days,
        minDays: shelfLife.min_days,
      }
    : null

const mapSurcharge = (surcharge: MenuRecipe['surcharge']): Recipe['surcharge'] =>
  surcharge
    ? {
        name: surcharge.name,
        price: surcharge.price,
        pricePerPortion: surcharge.price_per_portion,
      }
    : null

export const fromAPIRecipe = (recipe: MenuRecipe): Recipe => ({
  id: recipe.id as RecipeUUID,
  coreId: recipe.core_recipe_id,
  name: recipe.name,
  dietaryClaims: recipe.dietary_claims,
  dietType: recipe.diet_type || null,
  fiveADay: recipe.five_a_day,
  foodBrand: recipe.food_brand,
  images: recipe.images,
  invalidPreference: recipe.invalid_preference,
  isAvailable: recipe.is_available,
  isOffered: true, // Recipes returned from the API are offered by definition
  merchandisingTags: mapMerchandisingTags(recipe.merchandising_tags),
  nutritionalInformation: mapNutritionalInformation(recipe.nutritional_information),
  prepTime: recipe.prep_time,
  portions: mapPortions(recipe.portions),
  preparationType: recipe.preparation_type ?? null,
  rating: recipe.rating,
  shelfLife: mapShelfLife(recipe.shelf_life),
  spiceLevel: recipe.spice_level,
  surcharge: mapSurcharge(recipe.surcharge),
})

export const transformMenuRecipeTileResponseToRecipe = (
  recipeTile: MenuRecipeTileResponse,
): Recipe => ({
  id: recipeTile.id,
  coreId: recipeTile.core_recipe_id,
  name: recipeTile.name,
  dietaryClaims: recipeTile.dietary_claims,
  // Menu v3 recipe tiles do not return diet_type; tile rendering uses dietaryClaims instead.
  dietType: null,
  fiveADay: recipeTile.five_a_day,
  foodBrand: recipeTile.food_brand,
  images: recipeTile.images,
  invalidPreference: recipeTile.invalid_preference,
  isAvailable: recipeTile.is_available,
  isOffered: true,
  merchandisingTags: mapMerchandisingTags(recipeTile.merchandising_tags),
  nutritionalInformation: mapNutritionalInformation(recipeTile.nutritional_information),
  prepTime: recipeTile.prep_time,
  portions: mapPortions(recipeTile.portions),
  preparationType: recipeTile.preparation_type,
  rating: recipeTile.rating,
  shelfLife: mapShelfLife(recipeTile.shelf_life),
  spiceLevel: recipeTile.spice_level,
  surcharge: mapSurcharge(recipeTile.surcharge),
})
